import {Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ActivatedRoute, Router} from '@angular/router';
import {CustomerOverViewService} from '~features/customer-overview/customer-overview.service';
import {TablesTranslations} from '~shared/components/tables/tables-translations.service';
import {ActionButton, ActionButtonEvent, Bank, Customer} from '~core/models';
import {BankService} from '~core/services';
import {ActionButtonType} from '~core/enum';
import {TooltipTranslationService} from '~shared/translations';

/**
 * Handles bank data management.
 */
@UntilDestroy()
@Component({
  selector: 'app-bank-data-list',
  templateUrl: './bank-data-list.component.html',
  styleUrls: ['../../../customer-overview.component.scss']
})
export class BankDataListComponent implements OnInit, OnDestroy {
  public bankList: Array<Bank>;
  public columns: Array<string> = [];
  public actionButtons: Array<ActionButton> = [];
  public keys: Array<Array<string>> = [];

  @Input() public customer: Customer;
  @Input() public isPanelOpened: boolean = true;
  @Output() public isPanelOpenedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    public customerService: CustomerOverViewService,
    public tablesTranslations: TablesTranslations,
    private bankService: BankService,
    private router: Router,
    private route: ActivatedRoute,
    public tooltipTranslations: TooltipTranslationService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.bankService.castBankList.pipe(untilDestroyed(this))
      .subscribe(data => this.bankList = data);
    this.columns.push(this.tablesTranslations.i18nIBAN, this.tablesTranslations.i18nBIC, this.tablesTranslations.i18nBankName, '');
    this.keys.push(['IBAN', 'BIC', 'bankName', 'isTixxSelect']);
  }

  public ngOnDestroy(): void {
  }

  /**
   * Create the form and fetch the current user
   */
  public ngOnInit(): any {
    this.actionButtons.push(
      new ActionButton({
        type: ActionButtonType.REDIRECT,
        icon: '#esf-icon-euro',
        tooltip: this.tooltipTranslations.i18nViewSepaMandates,
        hideKey: 'sepaMandates',
      })
    );

    this.bankService.setBankList(this.customer.bankAccounts);
  }

  /**
   * Open the create address modal and pass the customer to it.
   */
  public createBankData(): void {
    this.customerService.toggleAllModal(false);
    this.customerService.toggleCreateBankingDataModal(true);
  }

  /**
   * Open the edit address modal and pass the address data to it.
   */
  public editBankData(bank: Bank): any {
    this.customerService.toggleAllModal(false);
    this.customerService.toggleEditBankingDataModal(true);
    this.bankService.announceUpdatedBankData(bank);
  }

  /**
   * Starts the delete bank data process.
   */
  public deleteBankDataModal(bankId: number): any {
    this.customerService.toggleDeleteModal(true, 'bank data', bankId);
  }

  /**
   * Delete a bank data.
   */
  public deleteBankData(bankId: number): any {
    this.customerService.toggleLoadingSubmitClicked(true);
    this.bankService.delete(bankId).pipe(untilDestroyed(this))
      .subscribe(() => {
        this.customerService.toggleLoadingSubmitClicked(false);
        this.customerService.toggleAllModal(false);
        this.customerService.toggleDeleteModal(false, '', null);
        this.bankService.deleteBankFromList(bankId);
      });
  }

  /**
   * Checks which action button was clicked
   */
  public actionButtonClicked(actionButton: ActionButtonEvent<Bank>): void {
    if (actionButton.type === ActionButtonType.REDIRECT) {
      this.bankService.setSepaMandateList(actionButton.data.sepaMandates);
      this.router.navigate(['sepa-mandates', actionButton.data.id], {relativeTo: this.route});
    }
  }
}
